import { logger } from '@treneo/shared/logger';

interface CachedValue<T> {
  value: T;
  expiresAt: number;
}

interface AuthMetadata {
  isAuthenticated: boolean;
  lastCheck: number;
}

export const setLocalStorageItem = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    logger.warn(`Failed to save ${key} to localStorage:`, error);
  }
};

export const getLocalStorageItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    logger.warn(`Failed to read ${key} from localStorage:`, error);
    return null;
  }
};

export const removeLocalStorageItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    logger.warn(`Failed to remove ${key} from localStorage:`, error);
  }
};

const isExpired = (expiresAt: number): boolean => Date.now() >= expiresAt;

const serializeWithTtl = <T>(value: T, ttlMs: number): string => {
  const cached: CachedValue<T> = {
    value,
    expiresAt: Date.now() + ttlMs
  };

  return JSON.stringify(cached);
};

const deserializeWithTtl = <T>(serialized: string): T | null => {
  try {
    const cached = JSON.parse(serialized) as CachedValue<T>;
    if (isExpired(cached.expiresAt)) {
      return null;
    }

    return cached.value;
  } catch (error) {
    logger.warn('Failed to deserialize TTL cache:', error);
    return null;
  }
};

export const setItemWithTtl = <T>(key: string, value: T, ttlMs: number): void => {
  try {
    const serialized = serializeWithTtl(value, ttlMs);
    setLocalStorageItem(key, serialized);
  } catch (error) {
    logger.warn(`Failed to save ${key} to localStorage with TTL:`, error);
  }
};

export const getItemWithTtl = <T>(key: string): T | null => {
  try {
    const serialized = getLocalStorageItem(key);
    if (!serialized) {
      return null;
    }

    return deserializeWithTtl<T>(serialized);
  } catch (error) {
    logger.warn(`Failed to read ${key} from localStorage with TTL:`, error);
    return null;
  }
};

export const removeItemWithTtl = (key: string): void => {
  removeLocalStorageItem(key);
};

export const cleanupExpiredItems = (keys: string[]): void => {
  try {
    for (const key of keys) {
      const serialized = getLocalStorageItem(key);
      if (!serialized) {
        continue;
      }

      try {
        const cached = JSON.parse(serialized) as CachedValue<unknown>;
        if (isExpired(cached.expiresAt)) {
          removeLocalStorageItem(key);
        }
      } catch {
        continue;
      }
    }
  } catch (error) {
    logger.warn('Failed to cleanup expired localStorage items:', error);
  }
};

export const saveAuthMetadataToLocalStorage = (metadata: AuthMetadata): void => {
  setLocalStorageItem('auth_metadata', JSON.stringify(metadata));
};

export const getAuthMetadataFromLocalStorage = (): AuthMetadata | null => {
  const stored = getLocalStorageItem('auth_metadata');
  if (!stored) {
    return null;
  }

  try {
    return JSON.parse(stored) as AuthMetadata;
  } catch (error) {
    logger.warn('Failed to parse auth metadata from localStorage:', error);
    return null;
  }
};

const PREFERENCE_KEYS_TO_CLEANUP = [
  'theme',
  'timezone',
  'coursesViewMode',
  'paginationSettings',
  'tagsSettings',
  'dateTimePickerSettings',
  'courseListViewMode'
] as const;

export const cleanupExpiredPreferences = (): void => {
  cleanupExpiredItems([...PREFERENCE_KEYS_TO_CLEANUP]);
};
